import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import './styles.css'; // Import the global CSS file
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import './registerServiceWorker'; // Import the service worker registration
import {
  manifestHrefForIconRefresh,
  pointManifestLink,
} from './utils/pwaForceUpdate';
import {
  clearPwaUpdateFlagIfExpired,
  isPwaUpdateCooldown,
} from './utils/pwaUpdate';

// Stamp the manifest URL so Android's installed app can see a new icon
// after this shell is the one actually running.
pointManifestLink(
  manifestHrefForIconRefresh(process.env.VUE_APP_VERSION),
  typeof document !== 'undefined' ? document : null,
);

// Check the environment variable to determine if we should show errors
const showErrors = process.env.VUE_APP_SHOW_ERRORS === 'true';

// Store the original console.error method
const originalConsoleError = console.error;

// Override console.error globally
console.error = function (...args) {
  if (showErrors) {
    // Only log the errors if showErrors is true
    originalConsoleError.apply(console, args);
  }
  // If showErrors is false, console.error will be suppressed
};

function getFormattedTimestamp() {
  const now = new Date();

  // Extract date components
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const day = String(now.getDate()).padStart(2, '0');
  const year = String(now.getFullYear()).slice(-2); // Get last two digits of the year

  // Extract time components
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  // Format as MM/DD/YY : HH/MM/SS
  return `${month}/${day}/${year} @ ${hours}:${minutes}:${seconds}`;
}

// Store the original console.log function
const originalConsoleLog = console.log;

// Create a custom log function
console.log = function (...args) {
  // Get the current timestamp
  const timestamp = getFormattedTimestamp();

  // Use the error stack to determine the module or component name
  const stack = new Error().stack.split('\n');
  const component = stack[2]?.trim().match(/at (\S+)/)?.[1] || 'unknown_module';

  // Prefix each log with the timestamp and module name
  originalConsoleLog(`[${timestamp}] [${component}]`, ...args);
};

// Service Worker: reload once when a new controller takes over (avoid reload loops).
// Coordinated with applyPwaUpdate (pixie-pwa-updating) so Install does not double-reload
// into a half-cleared cache (blank grey PWA shell after deploy).
if ('serviceWorker' in navigator) {
  let swRefreshPending = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isPwaUpdateCooldown()) {
      // Install already navigates; a new SW claiming a few seconds later
      // must not bounce the user through another reload.
      return;
    }
    if (swRefreshPending) return;
    swRefreshPending = true;
    console.log('Service worker updated. Reloading the page...');
    window.location.reload();
  });

  // Push notification click: navigate inside the existing PWA window (iOS-safe).
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || data.type !== 'pixie-navigate' || !data.url) return;
    try {
      const url = new URL(data.url, window.location.origin);
      if (url.origin !== window.location.origin) return;
      router.push(url.pathname + url.search + url.hash);
    } catch (_) {
      /* ignore */
    }
  });

  navigator.serviceWorker.ready.then(async () => {
    console.log('Service Worker is active and controlling the application.');
    // After deploy, applyPwaUpdate unregisters the SW and the push subscription
    // is dropped even though permission is still granted. Recreate it.
    try {
      const { notifyPushRestored, subscribePushIfGranted } = await import(
        './utils/chatPush'
      );
      const restored = await subscribePushIfGranted();
      notifyPushRestored(restored);
    } catch (_) {
      /* ignore */
    }
  });
}

// Create and mount the Vue application
createApp(App).use(router).use(FloatingVue).mount('#app');

// Signal successful boot so index.html recovery does not wipe a healthy session.
try {
  window.__PIXIE_APP_READY__ = true;
  sessionStorage.removeItem('pixie-boot-recovery');
  sessionStorage.removeItem('pixie-boot-recovery-at');
  clearPwaUpdateFlagIfExpired();
  // Drop one-shot recovery / update query params without another navigation loop.
  if (
    window.history &&
    (window.location.search.includes('_recover=') ||
      window.location.search.includes('_swu='))
  ) {
    const clean = new URL(window.location.href);
    clean.searchParams.delete('_recover');
    clean.searchParams.delete('_swu');
    window.history.replaceState(
      window.history.state,
      '',
      clean.pathname + clean.search + clean.hash,
    );
  }
} catch (_) {
  /* ignore */
}
